<template>
  <div>
    
    <!-- page banner -->
    <page-banner
      v-if="tempQuote"
      :title="pageTitle"
      :sub-title="$t('airportparking.results.subtitle')"
      :promo-box-placeholder="$t('airportparking.results.promo.placeholder')"
      :promo-box-active="promoActive"
      :promo-box-invalid="promoInvalid && initialPromo.length > 0 && !loading"
      :loading="loading"
    />

    <!-- results -->
    <div class="main-area container list_style">

      <div class="main-holder content_holder padding-style" :class="{ 'results' : false }">
 
        <!-- filters -->
        <parking-filter v-if="tempQuote" v-model="tempQuote" />

        <!-- map -->
        <parking-map v-if="filters.map && filteredResults" v-model="filteredResults" />

        <!-- hotel to parking quote -->
        <div class="hotel-detail pb-2 pt-5" v-if="tempQuote.hotelToParking === true && results.products && results.products.airportparking">
          <h2>{{ $t('airportparking.results.no_hotels') }}.</h2>
          <ul class="head-list list-unstyled">
            <li><i class="icon-parking"></i>{{ $t('airportparking.results.cars_available_1') }} {{ results.products.airportparking.length }} {{ $t('airportparking.results.cars_available_2') }}.</li>
          </ul>
        </div>

        <!-- product list -->
        <div v-if="filteredResults && filteredResults.length > 0 && tempQuote">
          <parking-item
            v-for="(group, key) in filteredResults" 
            :key="key"
            :airport="results.airport"
            :products="group.products"
            :product="group.product"
            :featured="featured"
          />
        </div>

        <!-- no results -->
        <div class="text-center" v-else-if="results && results.products">
          <div v-if="isFiltered">
            <h3 class="mt-5 mb-0">{{ $t('airportparking.results.filters.empty') }}</h3>
            <button @click="clearFilters" class="btn btn-secondary mt-3" v-if="results && results.products && results.products.airportparking">
              {{ $t('airportparking.results.filters.clear') }}
            </button>
          </div>
          <div v-else-if="!loading">
            <h3 class="mt-5 mb-0">{{ $t('airportparking.results.filters.empty') }}</h3>
          </div>
          <div v-else-if="loading">
            <h3 class="mt-5 mb-0">{{ $t('airportparking.results.filters.searching') }}</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- timeout modal -->
    <timeout v-if="tempQuote" />

    <!-- fancy loader -->
    <steps-loader :loading="loading" />

    <!-- more info -->
    <more-info />

    <!-- reveal -->
    <reveal />

    <!-- save for later -->
    <save-for-later />

  </div>
</template>

<script>
import { sync, call, get } from "vuex-pathify";
import { eventBus } from "@/eventBus";
import mixins from "@/mixins";
import { ParkingFilter, ParkingItem, ParkingMap, PageBanner, Timeout, MoreInfo, Reveal, SaveForLater, StepsLoader } from "@/components";
import group from "@/utils/productGroup";
import filterAndSort from "@/utils/parkingFilters";

export default {
  name: 'parking-results-view',
  components: { ParkingFilter, ParkingItem, ParkingMap, PageBanner, Timeout, MoreInfo, Reveal, SaveForLater, StepsLoader },
  mixins: [mixins.load, mixins.quoteGen, mixins.pageview],
  data() {
    return {
      showModal: false,
      modalItem: null,
      initialPromo: '',
      featured: null,
    };
  },
  computed: {
    tempQuote: sync("quoteTemp"),
    results: sync("results"),
    upgrades: sync("upgrades"),
    filters: sync("filters"),
    moreInfo: sync("modals/moreInfo"),
    needVehicle: sync("payment/needVehicle"),
    pageTitle: get("results/pageTitle"),
    promoActive: get("results/promoActive"),
    promoInvalid: get("results/promoInvalid"),
    isFiltered: get("filters/isFiltered"),
    promo: get("quoteTemp@promo"),
    filteredResults() {
      this.fakeLoad();
      if(!this.results.products) return false;
      let filtered = group(this.results.products.airportparking);

      // sort grouped products
      filtered.forEach(item => item.products.sort((a,b) => parseFloat(a.price) - parseFloat(b.price)))

      return filterAndSort(filtered, this.filters, this.featured);
    },
  },
  methods: {
    load: call("results/getResults"),
    loadUpgrades: call("upgrades/getUpgrades"),
    addToCart: call("cart/addToCart"),
    updateQuote: call("quote/updateQuote"),
    clearFilters() {
      this.filters.type = false;
      this.filters.terminal = false;
    }
  },
  watch: {
    'tempQuote.promo': {
      handler(value) {
        if(value.length > 0) this.initialPromo = value;
      }
    }
  },
  mounted() {
    
    this.featured = this.$route.query.productId;

    this.initialPromo = this.tempQuote.promo;

    let upgrades = (process.env.VUE_APP_UPGRADES === 'true') || false;

    eventBus.$on("SEARCH",  () => {

      // check for invalid date/times 
      if(this.tempQuote.dateA === this.tempQuote.dateB && this.tempQuote.timeA >= this.tempQuote.timeB) {
        return alert(this.$t('airportparking.results.filters.invalid_times'))
      }

      this.loading = true;
      this.tempQuote._timestamp = new Date();
      this.initialPromo = this.tempQuote.promo;

      this.$router.push({ query: this.tempQuote }, () => {})
      
      this.updateQuote({...this.tempQuote, hotelToParking: false }).then(() => {
        this.load("silent").then(() => {
          this.loading = false;
          this.sendPageView();
          
          if(upgrades) this.loadUpgrades({ agent: 'Lounges', category: 'airportlounge', adult: 1 });
        });
      });
    });

    eventBus.$once("SELECTED", value => {
      this.needVehicle = true;

      this.addToCart({ category: 'airportparking', ...value }).then(() => {
        this.moreInfo = false;
        
        let productId = value.product_id || null
        let quote = this.tempQuote;

        // eslint-disable-next-line
        let { _timestamp, quoteID, manual_discount, transfer_type, resortA, serviceCode, ...params } = quote;

        this.$router.push({ path: (upgrades && this.upgrades.products['airportlounge']) ? '/upgrades' : '/payment', query: { ...params, productId } }, () => {});
      });
    });

    eventBus.$on("MORE_INFO", value => {
      this.moreInfo = value;
    });
  },
  beforeDestroy() {
    eventBus.$off(['SEARCH', 'MORE_INFO']);
  },
};
</script>